// AI生成のユーティリティ関数
import type { AITreeResponse, WordNode } from '../types';
import { getTreeStructure, selectNode } from '../services/tauri-api';

/**
 * 0-0ルート（最確率ルート）かどうかを判定
 */
function isZeroZeroPath(path: string): boolean {
  const parts = path.split('.');
  return parts.every(part => part === '0');
}

/**
 * ノードから0.0パスの次のノード（最確率の子ノード）を見つける
 */
function findNextZeroZeroNode(node: WordNode): WordNode | null {
  if (node.children.length === 0) {
    return null;
  }

  let bestNode: WordNode | null = null;
  let bestProb = 0;

  for (const child of node.children) {
    // 0.0パス上のノード（パスの最後が0）で最確率のものを探す
    if (isZeroZeroPath(child.path)) {
      if (child.prob > bestProb) {
        bestProb = child.prob;
        bestNode = child;
      }
    }
  }

  return bestNode;
}

/**
 * ツリーから指定されたパスのノードを見つける
 */
function findNodeByPath(tree: AITreeResponse, path: string): WordNode | null {
  if (tree.path === path) {
    return {
      path: tree.path,
      word: tree.word,
      prob: tree.prob,
      is_selected: tree.is_selected,
      is_skipped: tree.is_skipped,
      is_custom: tree.is_custom,
      isVisible: tree.isVisible,
      children: tree.children,
    };
  }

  function searchInChildren(children: WordNode[], targetPath: string): WordNode | null {
    for (const child of children) {
      if (child.path === targetPath) {
        return child;
      }
      if (targetPath.startsWith(child.path + '.')) {
        const found = searchInChildren(child.children, targetPath);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  return searchInChildren(tree.children, path);
}

/**
 * 0.0パスを進んで自動生成を実行
 * @param sessionId セッションID
 * @param maxSteps 最大ステップ数（デフォルト: 60）
 * @param onProgress 進捗コールバック（オプション）
 * @returns 生成が完了したかどうか
 */
export async function generateZeroZeroPath(
  sessionId: string,
  maxSteps: number = 60,
  onProgress?: (step: number, currentNodeId: string) => void
): Promise<{ success: boolean; error?: string; completed: boolean }> {
  let currentNodeId = '0'; // ルートノードから開始
  let step = 0;

  try {
    while (step < maxSteps) {
      // ツリー構造を取得
      const treeResponse = await getTreeStructure(sessionId);
      if (!treeResponse.success || !treeResponse.data) {
        return {
          success: false,
          error: treeResponse.error || 'Failed to get tree structure',
          completed: false,
        };
      }

      const tree = treeResponse.data;

      // 現在のノードを見つける
      const currentNode = findNodeByPath(tree, currentNodeId);
      if (!currentNode) {
        return {
          success: false,
          error: `Node "${currentNodeId}" not found`,
          completed: false,
        };
      }

      // 0.0パスの次のノードを探す
      const nextNode = findNextZeroZeroNode(currentNode);

      if (nextNode) {
        // 次のノードが存在する場合、そのノードに移動
        currentNodeId = nextNode.path;
        step++;
        if (onProgress) {
          onProgress(step, currentNodeId);
        }
        continue;
      }

      // 次のノードが存在しない場合、現在のノードを選択して候補を生成
      if (!currentNode.is_selected) {
        console.log(`[Generation] Selecting node ${currentNodeId} to generate candidates`);
        const selectResult = await selectNode(sessionId, currentNodeId);
        
        if (!selectResult.success) {
          // エラーが発生した場合
          if (selectResult.error?.includes('ダミー候補')) {
            // ダミー候補が検出された場合は正常終了
            console.log('[Generation] Dummy candidate detected, generation completed');
            return {
              success: true,
              completed: true,
            };
          }
          return {
            success: false,
            error: selectResult.error || 'Failed to select node',
            completed: false,
          };
        }

        // 選択後、再度ツリーを取得して次のノードを探す
        const updatedTreeResponse = await getTreeStructure(sessionId);
        if (!updatedTreeResponse.success || !updatedTreeResponse.data) {
          return {
            success: false,
            error: 'Failed to get updated tree structure',
            completed: false,
          };
        }

        const updatedTree = updatedTreeResponse.data;
        const updatedCurrentNode = findNodeByPath(updatedTree, currentNodeId);
        if (!updatedCurrentNode) {
          return {
            success: false,
            error: `Node "${currentNodeId}" not found after selection`,
            completed: false,
          };
        }

        // 再度0.0パスの次のノードを探す
        const newNextNode = findNextZeroZeroNode(updatedCurrentNode);
        if (newNextNode) {
          currentNodeId = newNextNode.path;
          step++;
          if (onProgress) {
            onProgress(step, currentNodeId);
          }
          continue;
        } else {
          // 候補を生成したが、0.0パスの次のノードが見つからない場合、終了
          console.log('[Generation] No next node found after selection, generation completed');
          return {
            success: true,
            completed: true,
          };
        }
      } else {
        // 既に選択済みの場合、次のノードが見つからない場合は終了
        console.log('[Generation] Node already selected and no next node found, generation completed');
        return {
          success: true,
          completed: true,
        };
      }
    }

    // 最大ステップ数に達した場合
    console.log(`[Generation] Reached max steps (${maxSteps}), generation completed`);
    return {
      success: true,
      completed: true,
    };
  } catch (error) {
    console.error('[Generation] Error during generation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      completed: false,
    };
  }
}

